import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Save, Sparkles, Play, Clock, CheckCircle2, XCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { format } from "date-fns";
import { LANGUAGES } from "@/lib/languages";

type AutoConfig = {
  enabled?: boolean;
  type?: "ai" | "rss" | "perplexity" | "mixed";
  deploymentStrategy?: "manual" | "semi_auto" | "auto_publish";
  syncFrequency?: string;
  language?: string;
  strictLanguage?: boolean;
  categories?: string[];
  count?: number;
  keywords?: string[];
  sourcePriority?: ("rss" | "perplexity" | "ai")[];
  intervalHours?: number;
  autoReview?: boolean;
  dedupe?: boolean;
  minScore?: number;
  publishWithoutAiRewrite?: boolean;
};

type Props = { siteId: string };

const TYPE_OPTIONS = [
  { value: "ai", label: "AI-generated only" },
  { value: "rss", label: "RSS feeds" },
  { value: "perplexity", label: "Perplexity discovery" },
  { value: "mixed", label: "Mixed (best of all)" },
];

const LANG_OPTIONS = LANGUAGES.map((l) => ({ value: l.code, label: `${l.flag} ${l.label}` }));

export default function AutoPublishConfigPanel({ siteId }: Props) {
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [keywordInput, setKeywordInput] = useState("");
  const [config, setConfig] = useState<AutoConfig>({});

  const { data: website } = useQuery({
    queryKey: ["website-autopublish", siteId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("websites")
        .select("company_id, automation_config, content_config, deployment_strategy, sync_frequency")
        .eq("id", siteId)
        .single();
      if (error) throw error;
      return data;
    },
  });

  useEffect(() => {
    if (website) {
      setConfig({
        ...((website.automation_config as AutoConfig | null) ?? {}),
        deploymentStrategy: (website as any).deployment_strategy ?? "manual",
        syncFrequency: (website as any).sync_frequency ?? "24h",
      });
    }
  }, [website]);

  const { data: categories = [] } = useQuery({
    queryKey: ["categories-all"],
    queryFn: async () => {
      const { data } = await supabase.from("categories").select("id, name").order("name");
      return data || [];
    },
  });

  const { data: jobs = [] } = useQuery({
    queryKey: ["autopublish-jobs", siteId],
    queryFn: async () => {
      const { data } = await supabase
        .from("autopublish_jobs")
        .select("id, status, scheduled_for, completed_at, error_message, article_id")
        .eq("website_id", siteId)
        .order("scheduled_for", { ascending: false })
        .limit(10);
      return data || [];
    },
    refetchInterval: 5000,
  });

  const update = (patch: Partial<AutoConfig>) => setConfig((p) => ({ ...p, ...patch }));

  const toggleCategory = (id: string) => {
    const current = config.categories || [];
    update({ categories: current.includes(id) ? current.filter((x) => x !== id) : [...current, id] });
  };

  const addKeyword = () => {
    const kw = keywordInput.trim().toLowerCase();
    if (!kw) return;
    const current = config.keywords || [];
    if (!current.includes(kw)) update({ keywords: [...current, kw] });
    setKeywordInput("");
  };

  const removeKeyword = (kw: string) => {
    update({ keywords: (config.keywords || []).filter((k) => k !== kw) });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from("websites")
        .update({
          automation_config: config as any,
          deployment_strategy: config.deploymentStrategy ?? "manual",
          sync_frequency: config.syncFrequency ?? "24h",
        } as any)
        .eq("id", siteId);
      if (error) throw error;
      await (supabase as any)
        .from("autopublish_jobs")
        .update({
          trigger_type: config.enabled ? "auto" : "manual",
          input_data: {
            automation_config: config,
            deployment_strategy: config.deploymentStrategy ?? "manual",
            sync_frequency: config.syncFrequency ?? "24h",
          },
        })
        .eq("website_id", siteId)
        .in("status", ["queued", "scheduled", "pending"]);
      queryClient.invalidateQueries({ queryKey: ["website-autopublish", siteId] });
      queryClient.invalidateQueries({ queryKey: ["autopublish-jobs", siteId] });
      toast.success("Auto-publish settings saved");
    } catch {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleRunNow = async () => {
    setRunning(true);
    try {
      const { data: job, error } = await supabase
        .from("autopublish_jobs")
        .insert({
          website_id: siteId,
          company_id: (website as any)?.company_id,
          status: "queued",
          scheduled_for: new Date().toISOString(),
          trigger_type: "manual",
          input_data: {
            automation_config: config,
            publish_without_ai_rewrite: config.publishWithoutAiRewrite !== false,
            estimated_credits: config.publishWithoutAiRewrite === false ? "rewrite_model_cost" : 0,
            credit_reason: config.publishWithoutAiRewrite === false
              ? "AI rewrite/generation is enabled for this manual run"
              : "Publishing an existing reviewed article does not consume AI credits",
          },
        } as any)
        .select("id")
        .single();
      if (error) throw error;

      const { data: result, error: invokeErr } = await supabase.functions.invoke("autopublish-engine", {
        body: { mode: "drain", limit: 1, company_id: (website as any)?.company_id },
      });
      if (invokeErr) throw invokeErr;

      // Smart, user-friendly feedback
      if (result?.wordpress_pushed) {
        toast.success(
          result.status === "published"
            ? "Article published live to your site"
            : "Article saved as draft on your site (awaiting review)"
        );
      } else if (result?.wordpress_skipped) {
        toast.warning("Article created locally", {
          description: "Site connection is not set up yet. Add it in Site → Settings → Connection to push articles automatically.",
        });
      } else if (result?.wordpress_error) {
        toast.warning("Article created, but publishing failed", {
          description: result.wordpress_error,
        });
      } else {
        toast.success(config.publishWithoutAiRewrite === false ? "Article created" : "Reviewed article published without AI credits");
      }
      queryClient.invalidateQueries({ queryKey: ["autopublish-jobs", siteId] });
    } catch (e: any) {
      const raw = e?.message || "";
      // Translate technical errors to user-friendly ones
      let friendly = "Could not start auto-publish";
      let description: string | undefined;
      if (/rate limit/i.test(raw)) {
        friendly = "AI is busy right now";
        description = "Please wait a moment and try again.";
      } else if (/credits/i.test(raw)) {
        friendly = "AI credits exhausted";
        description = "This only applies when AI rewrite/generation is enabled. Publishing reviewed articles can run without credits.";
      } else if (/quality score/i.test(raw)) {
        friendly = "Article didn't meet quality standards";
        description = "Try adding more keywords or lowering the minimum quality score.";
      } else if (/duplicate/i.test(raw)) {
        friendly = "A similar article already exists";
        description = "Disable 'Skip duplicates' or change your topic to generate a new one.";
      } else if (raw) {
        description = raw;
      }
      toast.error(friendly, { description });
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <h3 className="text-sm font-semibold">Auto-Publish Configuration</h3>
          </div>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            Publish reviewed articles without AI credits, or explicitly enable AI rewrite/generation when needed.
          </p>
        </div>
        <Switch
          checked={config.enabled ?? false}
          onCheckedChange={(v) => update({ enabled: v })}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left column */}
        <div className="space-y-4 rounded-lg border border-border p-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Deployment strategy</Label>
              <Select value={config.deploymentStrategy || "manual"} onValueChange={(v) => update({ deploymentStrategy: v as AutoConfig["deploymentStrategy"] })}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual" className="text-xs">Manual</SelectItem>
                  <SelectItem value="semi_auto" className="text-xs">Review then publish</SelectItem>
                  <SelectItem value="auto_publish" className="text-xs">Auto publish</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Sync frequency</Label>
              <Select value={config.syncFrequency || "24h"} onValueChange={(v) => update({ syncFrequency: v })}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual" className="text-xs">Manual only</SelectItem>
                  <SelectItem value="5m" className="text-xs">Real time (every 5 min)</SelectItem>
                  <SelectItem value="15m" className="text-xs">Every 15 min</SelectItem>
                  <SelectItem value="1h" className="text-xs">Hourly</SelectItem>
                  <SelectItem value="6h" className="text-xs">Every 6 hours</SelectItem>
                  <SelectItem value="24h" className="text-xs">Daily</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Article type</Label>
            <Select value={config.type || "mixed"} onValueChange={(v) => update({ type: v as any })}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TYPE_OPTIONS.map((t) => (
                  <SelectItem key={t.value} value={t.value} className="text-xs">{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Language *</Label>
            <Select value={config.language || "en"} onValueChange={(v) => update({ language: v })}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {LANG_OPTIONS.map((l) => (
                  <SelectItem key={l.value} value={l.value} className="text-xs">{l.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center justify-between rounded-md border border-border p-2 mt-1.5">
              <div>
                <p className="text-[11px] font-medium">Strict language match</p>
                <p className="text-[10px] text-muted-foreground">Block cross-language publishing</p>
              </div>
              <Switch
                checked={config.strictLanguage ?? true}
                onCheckedChange={(v) => update({ strictLanguage: v })}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Articles per run: {config.count ?? 3}</Label>
            <Slider
              value={[config.count ?? 3]}
              min={1}
              max={20}
              step={1}
              onValueChange={(v) => update({ count: v[0] })}
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Schedule (every X hours): {config.intervalHours ?? 6}</Label>
            <Slider
              value={[config.intervalHours ?? 6]}
              min={1}
              max={24}
              step={1}
              onValueChange={(v) => update({ intervalHours: v[0] })}
            />
            <p className="text-[10px] text-muted-foreground">Runs are managed by the platform's hourly scheduler.</p>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Minimum quality score: {config.minScore ?? 5}</Label>
            <Slider
              value={[config.minScore ?? 5]}
              min={0}
              max={20}
              step={1}
              onValueChange={(v) => update({ minScore: v[0] })}
            />
            <p className="text-[10px] text-muted-foreground">Articles below this score are skipped.</p>
          </div>
        </div>

        {/* Right column */}
        <div className="space-y-4 rounded-lg border border-border p-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Categories ({(config.categories || []).length} selected)</Label>
            <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
              {categories.map((c: any) => {
                const active = (config.categories || []).includes(c.id);
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => toggleCategory(c.id)}
                    className={`rounded-full border px-2 py-0.5 text-[10px] font-medium transition-colors ${
                      active
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-muted text-muted-foreground border-border hover:bg-accent"
                    }`}
                  >
                    {c.name}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Topic keywords</Label>
            <div className="flex gap-1.5">
              <Input
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addKeyword())}
                placeholder="Add keyword and press Enter"
                className="h-7 text-xs"
              />
              <Button size="sm" variant="outline" onClick={addKeyword} className="h-7 text-xs">Add</Button>
            </div>
            {(config.keywords || []).length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {(config.keywords || []).map((kw) => (
                  <span key={kw} className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2 py-0.5 text-[10px]">
                    {kw}
                    <button onClick={() => removeKeyword(kw)} className="hover:text-destructive">×</button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Source priority</Label>
            <p className="text-[10px] text-muted-foreground">Order matters — first source is tried first.</p>
            <div className="flex flex-wrap gap-1.5">
              {(["rss", "perplexity", "ai"] as const).map((src) => {
                const arr = config.sourcePriority || ["rss", "perplexity", "ai"];
                const idx = arr.indexOf(src);
                return (
                  <span
                    key={src}
                    className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2 py-1 text-[10px] font-medium"
                  >
                    {idx >= 0 && <span className="text-primary">#{idx + 1}</span>}
                    {src.toUpperCase()}
                  </span>
                );
              })}
            </div>
          </div>

          <div className="flex items-center justify-between rounded-md border border-border p-2.5">
            <div>
              <p className="text-[11px] font-medium">Publish without AI rewrite</p>
              <p className="text-[10px] text-muted-foreground">Use reviewed articles that were already rewritten. Estimated cost: 0 credits.</p>
            </div>
            <Switch
              checked={config.publishWithoutAiRewrite !== false}
              onCheckedChange={(v) => update({ publishWithoutAiRewrite: v })}
            />
          </div>

          <div className="flex items-center justify-between rounded-md border border-border p-2.5">
            <div>
              <p className="text-[11px] font-medium">Manual review before publish</p>
              <p className="text-[10px] text-muted-foreground">Save as draft instead of publishing live</p>
            </div>
            <Switch
              checked={config.autoReview ?? true}
              onCheckedChange={(v) => update({ autoReview: v })}
            />
          </div>

          <div className="flex items-center justify-between rounded-md border border-border p-2.5">
            <div>
              <p className="text-[11px] font-medium">Skip duplicates</p>
              <p className="text-[10px] text-muted-foreground">Avoid republishing similar titles</p>
            </div>
            <Switch
              checked={config.dedupe ?? true}
              onCheckedChange={(v) => update({ dedupe: v })}
            />
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-2">
        <Button variant="outline" size="sm" onClick={handleRunNow} disabled={running}>
          {running ? <Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> : <Play className="h-3 w-3 mr-1.5" />}
          Run Now
        </Button>
        <Button size="sm" onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> : <Save className="h-3 w-3 mr-1.5" />}
          Save Configuration
        </Button>
      </div>

      {/* Recent jobs log */}
      <div className="rounded-lg border border-border">
        <div className="px-4 py-2 border-b border-border bg-muted/40">
          <h4 className="text-xs font-semibold">Recent Auto-Publish Jobs</h4>
        </div>
        <div className="divide-y divide-border max-h-64 overflow-y-auto">
          {jobs.length === 0 ? (
            <div className="p-4 text-center text-xs text-muted-foreground">No jobs yet</div>
          ) : jobs.map((j: any) => (
            <div key={j.id} className="px-4 py-2 flex items-center gap-3 text-xs">
              {j.status === "published" || j.status === "draft" ? (
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
              ) : j.status === "failed" ? (
                <XCircle className="h-3.5 w-3.5 text-destructive" />
              ) : (
                <Clock className="h-3.5 w-3.5 text-amber-500" />
              )}
              <span className="font-medium uppercase text-[10px] text-muted-foreground w-16">{j.status}</span>
              <span className="text-muted-foreground flex-1">
                {format(new Date(j.scheduled_for), "MMM d, HH:mm")}
              </span>
              {j.error_message && <span className="text-destructive text-[10px] truncate max-w-xs">{j.error_message}</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
